import { debounce } from '@mui/material';
import { canadaProvinces, usStates } from '@provision/location-utils';
import { useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Address } from 'src/graphql/entities/address';
import { mapPlatformUsersToSortedOptions } from 'src/graphql/entities/platform-user/platform-user.mapping';
import { useSearch } from 'src/graphql/entities/search/use-search';
import { EntityNames } from 'src/graphql/types';
import { useGoogleMapsSuggestions, useScrollPaginateHandler } from 'src/utils/hooks';

export enum AddSiteFormFields {
  Address1 = 'address1',
  Address2 = 'address2',
  City = 'city',
  State = 'state',
  Zip = 'zip',
  BusinessName = 'businessName',
  ClientSegment = 'clientSegment',
  Clients = 'clients',
  AccountManager = 'accountManager',
  OrgNotes = 'orgNotes',
  Partner = 'partner',
}

export type ClientOption = {
  id: string;
  label: string;
};

export interface NewAddressFormValues {
  [AddSiteFormFields.Partner]: { id: number; label: string } | null;
  [AddSiteFormFields.Address1]: string;
  [AddSiteFormFields.Address2]: string;
  [AddSiteFormFields.City]: string;
  [AddSiteFormFields.State]: { id: string; value: string; label: string } | null;
  [AddSiteFormFields.Zip]: string;
  [AddSiteFormFields.BusinessName]: string;
  [AddSiteFormFields.ClientSegment]: { id: string; label: string } | null;
  [AddSiteFormFields.Clients]: ClientOption[];
  [AddSiteFormFields.AccountManager]: { id: string; label: string; value: string } | null;
  [AddSiteFormFields.OrgNotes]: string;
}

export const statesOptions = [...usStates, ...canadaProvinces].map((state) => ({
  id: state.value,
  value: state.value,
  label: `${state.text} (${state.value})`,
}));

export const getClientOptionLabel = (client: { fullName: string; primaryEmail?: string }) =>
  `${client.fullName}${client.primaryEmail ? ` (${client.primaryEmail})` : ''}`;

export const getAddSiteDefaultValues = ({
  address,
  clients = [],
}: {
  address?: Address | null;
  clients?: ClientOption[];
}): NewAddressFormValues => ({
  [AddSiteFormFields.Partner]: null,
  [AddSiteFormFields.Address1]: address?.address1 ?? '',
  [AddSiteFormFields.Address2]: address?.address2 ?? '',
  [AddSiteFormFields.City]: address?.city ?? '',
  [AddSiteFormFields.State]:
    statesOptions.find((state) => state.value === address?.state) ?? null,
  [AddSiteFormFields.Zip]: address?.zip ?? '',
  [AddSiteFormFields.BusinessName]: address?.businessName ?? '',
  [AddSiteFormFields.ClientSegment]: address?.brandId
    ? { id: address.brandId, label: address.brand?.name ?? '' }
    : null,
  [AddSiteFormFields.Clients]: clients,
  [AddSiteFormFields.AccountManager]: address?.ownerId
    ? { id: address.ownerId, value: address.ownerId, label: address.ownerFullName ?? '' }
    : null,
  [AddSiteFormFields.OrgNotes]: address?.orgNotes ?? '',
});

const ACTIVE_USERS_FILTER = [{ attribute: 'isActive', operator: '=', value: 'true' }];

/** Server-side searches behind the site form's Account Manager, Clients and Address 1 fields. */
export function useSiteFormSearch(partnerId?: number | null) {
  const [searchUsersTerm, setSearchUsersTerm] = useState('');
  const [searchClientsTerm, setSearchClientsTerm] = useState('');
  const [address1Input, setAddress1Input] = useState('');

  const {
    records: activeUserRecords,
    isLoading: usersLoading,
    loadMore: usersLoadMore,
    hasMore: usersHasMore,
  } = useSearch({
    entityName: EntityNames.PlatformUser,
    searchTerm: searchUsersTerm,
    filter: ACTIVE_USERS_FILTER,
    partnerId,
    shouldIncludePlatformPartnerId: false,
    showResultsWhenEmptySearchTerm: true,
  }) ?? {};

  const userOptions = useMemo(
    () => mapPlatformUsersToSortedOptions(activeUserRecords) ?? [],
    [activeUserRecords],
  );

  const clientsFilter = useMemo(
    () => [
      // This filter should be present, since it is also used in multi-tenancy mode
      // when partner-id header is not included in the request.
      { attribute: 'partnerId', operator: '=', value: String(partnerId) },
    ],
    [partnerId],
  );

  const {
    records: clientRecords = [],
    loadMore: clientsLoadMore,
    hasMore: clientsHasMore,
    isLoading: clientsLoading,
  } = useSearch({
    entityName: EntityNames.Client,
    searchTerm: searchClientsTerm,
    filter: clientsFilter,
    showResultsWhenEmptySearchTerm: true,
    skip: isNullish(partnerId),
  }) ?? {};

  const clientOptions = useMemo(
    () =>
      clientRecords.map((client) => ({
        id: client.clientId,
        label: getClientOptionLabel(client),
      })),
    [clientRecords],
  );

  const usersHandleScroll = useScrollPaginateHandler({
    loadMore: usersLoadMore,
    enabled: usersHasMore,
    thresholdPx: 100,
  });

  const clientsHandleScroll = useScrollPaginateHandler({
    loadMore: clientsLoadMore,
    enabled: clientsHasMore,
    thresholdPx: 100,
  });

  const { suggestions } = useGoogleMapsSuggestions({ input: address1Input });

  const suggestionOptions = useMemo(
    () =>
      suggestions.map((suggestion) => ({
        ...suggestion,
        label: suggestion.description,
        id: suggestion.place_id,
      })),
    [suggestions],
  );

  const searchAddress1 = useMemo(
    () => debounce((value: string) => setAddress1Input(value), SEARCH_DEBOUNCE_DELAY),
    [],
  );

  return {
    users: {
      options: userOptions,
      loading: usersLoading,
      onScroll: usersHandleScroll,
      search: setSearchUsersTerm,
    },
    clients: {
      options: clientOptions,
      loading: clientsLoading,
      onScroll: clientsHandleScroll,
      search: setSearchClientsTerm,
    },
    address1: {
      options: suggestionOptions,
      search: searchAddress1,
    },
  };
}
